import React, { useState } from 'react';

import BulkUploadBlock from './bulk/BulkUploadBlock';
import AddEventForm from './events/AddEventForm';
import GhostButton from './GhostButton';
import SetupCard from './SetupCard';
import StepShell from './StepShell';
import useStepPayload from './useStepPayload';

import { useToast } from 'Hooks/useToast';

/**
 * Events: each added event as a card, "+ Add an event" by hand, and the events
 * template for bulk. Every write answers with the whole page.
 */
export default function EventsStep({ step, urls, deferring, saving, onDefer, onNext, onStatusChange }) {
    const { data, send, load } = useStepPayload(urls.data, onStatusChange);
    const [adding, setAdding] = useState(false);
    const toast = useToast();

    const lede = $t('Add your events. Once added, events are attached to venues in the next step, and sessions are scheduled against them after that.');

    const add = (body) =>
        send(() => axios.post(urls.items, body))
            .then(() => setAdding(false))
            .catch((e) => {
                if (e?.response?.status !== 422) {
                    toast.error(e?.response?.data?.message || $t('Something went wrong. Please try again.'));
                    load();
                }

                throw e;
            });

    const imported = (response) => {
        send(
            () => Promise.resolve({ data: response }),
            (body) => body.step,
        ).catch(() => load());
        toast.success(response.result.message);
    };

    if (!data) {
        return (
            <StepShell step={step} lede={lede} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    return (
        <StepShell step={step} lede={lede} deferring={deferring} saving={saving} onDefer={onDefer} onNext={onNext}>
            <div className="tw-flex tw-flex-col tw-gap-y-2 tw-max-w-2xl">
                {data.events.length === 0 && !adding && <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{$t('No events yet')}</p>}

                {data.events.map((event) => (
                    <SetupCard key={event.id} heading={event.name} chip={$t('Event')} />
                ))}

                {data.can_create && adding && <AddEventForm defaultSports={data.default_sports} onSubmit={add} onCancel={() => setAdding(false)} />}

                {data.can_create && (
                    <div className="tw-mt-1">
                        <GhostButton expanded={adding} onClick={() => setAdding((current) => !current)}>
                            {$t('+ Add an event')}
                        </GhostButton>
                    </div>
                )}
            </div>

            <BulkUploadBlock
                id="setup-wizard-events-bulk"
                templateUrl={urls.template}
                templateLabel={$t('Download the events template')}
                importUrl={urls.import}
                canImport={data.can_import}
                onImported={imported}
            />
        </StepShell>
    );
}
